import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  Divider,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import FormFieldEditor, { FormField, FormSection } from './FormFieldEditor';
import FormSectionEditor from './FormSectionEditor';
import { DEFAULT_PERIOD_CONFIG } from '@shared/control-period';
import DocumentRequirementEditor from './DocumentRequirementEditor';
import SortingTab from './SortingTab';
import { VersionedControlType, DocumentRequirement } from '@/types';
import type { SaveFormConfigurationInput } from '@/hooks/useSaveFormConfiguration';
import AppDatePicker from '@/components/common/AppDatePicker';

/** Everything one Spara writes; the caller persists it as a single unit. */
export type FormConfigurationDraft = Omit<SaveFormConfigurationInput, 'controlTypeId'>;

interface FormConfigurationBuilderProps {
  open: boolean;
  controlType: VersionedControlType | null;
  onClose: () => void;
  /** Resolves when persisted; rejects to keep the dialog open with its draft intact. */
  onSave: (draft: FormConfigurationDraft) => Promise<void>;
  isLoading?: boolean;
}

/** The part of the draft whose changes the builder alone can persist. */
const snapshotForm = (fields: FormField[], sections: FormSection[]): string =>
  JSON.stringify({ fields, sections });

const toRequirementSortOrders = (
  requirements: DocumentRequirement[],
): FormConfigurationDraft['requirementSortOrders'] =>
  requirements.map((requirement) => ({
    id: requirement.id,
    displayOrder: requirement.displayOrder ?? 0,
  }));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`form-config-tabpanel-${index}`}
      aria-labelledby={`form-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const FIELD_TEMPLATES: Partial<FormField>[] = [
  {
    type: 'text',
    label: 'Gårdnamn',
    placeholder: 'Ange gårdnamn',
    required: true,
    layoutWidth: 6,
  },
  {
    type: 'number',
    label: 'Areal (ha)',
    placeholder: '0',
    required: false,
    layoutWidth: 4,
  },
  {
    type: 'date',
    label: 'Datum',
    required: false,
    layoutWidth: 4,
  },
  {
    type: 'select',
    label: 'Kategori',
    options: [
      { value: 'option1', label: 'Alternativ 1' },
      { value: 'option2', label: 'Alternativ 2' },
    ],
    required: false,
    layoutWidth: 6,
  },
  {
    type: 'textarea',
    label: 'Beskrivning',
    placeholder: 'Beskriv...',
    required: false,
    layoutWidth: 12,
  },
  {
    type: 'boolean',
    label: 'Ja/Nej fråga',
    required: false,
    layoutWidth: 6,
  },
];

const FormConfigurationBuilder: React.FC<FormConfigurationBuilderProps> = ({
  open,
  controlType,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [fields, setFields] = useState<FormField[]>([]);
  const [sections, setSections] = useState<FormSection[]>([]);
  const [documentRequirements, setDocumentRequirements] = useState<DocumentRequirement[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  // Fields and sections are compared against the last saved snapshot. Requirement
  // *content* is persisted by DocumentRequirementEditor as it goes, so only a
  // reorder from the Sortering tab counts as unsaved for requirements.
  const [savedFormSnapshot, setSavedFormSnapshot] = useState('');
  const [requirementOrderDirty, setRequirementOrderDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'saveAndClose' | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const isDirty = requirementOrderDirty || snapshotForm(fields, sections) !== savedFormSnapshot;

  // Initialize fields and sections when modal opens
  React.useEffect(() => {
    if (controlType && open) {
      const existingFields = controlType.formConfiguration?.fields || [];
      const existingSections = controlType.formConfiguration?.sections || [];
      
      // Convert existing fields to FormField format
      const convertedFields: FormField[] = existingFields.map((field: any) => ({
        id: field.id,
        type: field.type,
        label: field.label,
        placeholder: field.placeholder,
        description: field.description,
        options: field.options,
        defaultValue: field.defaultValue,
        required: field.required || false,
        layoutWidth: field.layoutWidth || (field.type === 'textarea' ? 12 : 6), // Default based on type
        sectionId: field.sectionId,
        displayOrder: field.displayOrder,
        unit: field.unit,
        optionStyle: field.optionStyle,
        inputPlacement: field.inputPlacement,
        // Without this the rule is silently dropped on the next save.
        filterRule: field.filterRule ?? null,
        allowUpload: field.allowUpload ?? false,
        uploadTags: field.uploadTags,
        validityMode: field.validityMode,
      }));
      
      // Convert existing sections to FormSection format
      const convertedSections: FormSection[] = existingSections.map((section: any) => ({
        id: section.id,
        title: section.title,
        description: section.description,
        defaultExpanded: section.defaultExpanded !== false, // Default to true
        order: section.order || 0,
      }));
      
      // Convert existing document requirements
      // Document requirements are returned at the top level by the backend
      const existingRequirements = controlType.documentRequirements || [];

      const convertedRequirements: DocumentRequirement[] = existingRequirements.map((req: any) => ({
        id: req.id,
        tags: req.tags ?? [],
        validityMode: req.validityMode ?? null,
        isRequired: req.isRequired || false,
        validityPeriod: req.validityPeriod,
        yearOffsets: req.yearOffsets ?? [],
        coverageRule: req.coverageRule ?? 'ALL_YEARS',
        description: req.description || '',
        informationText: req.informationText,
        sectionName: req.sectionName,
        displayOrder: req.displayOrder,
        // The editor round-trips this on save; dropping it here meant every
        // saved requirement lost the rule the AG-249 migration copied down.
        filterRule: req.filterRule ?? null,
      }));

      // Auto-create form sections from document requirement sectionNames
      // so they appear in the section dropdown for form field assignment
      const docReqSectionNames = new Set(
        existingRequirements
          .map((req: any) => req.sectionName)
          .filter(Boolean) as string[]
      );
      const existingSectionTitles = new Set(convertedSections.map((s) => s.title));
      let nextOrder = convertedSections.length;
      docReqSectionNames.forEach((name) => {
        if (!existingSectionTitles.has(name)) {
          convertedSections.push({
            id: `section_docreq_${name.replace(/\s+/g, '_').toLowerCase()}`,
            title: name,
            description: '',
            defaultExpanded: true,
            order: nextOrder++,
          });
        }
      });

      setFields(convertedFields);
      setSections(convertedSections);
      setDocumentRequirements(convertedRequirements);
      setSavedFormSnapshot(snapshotForm(convertedFields, convertedSections));
      setRequirementOrderDirty(false);
    }
  }, [controlType, open]);

  const addField = useCallback((template?: Partial<FormField>) => {
    setFields(prev => {
      // Assign an order past everything else so a new field lands last rather than
      // tying at 0 and jumping to the top. Fine-tuned in the Sortering tab.
      const nextOrder = prev.reduce(
        (max, f) => Math.max(max, f.displayOrder ?? -1),
        -1,
      ) + 1;

      const newField: FormField = {
        id: `field_${Date.now()}`,
        type: 'text',
        label: 'Nytt fält',
        required: false,
        layoutWidth: 6, // Default to half width
        displayOrder: nextOrder,
        ...template,
      };

      return [...prev, newField];
    });
  }, []);

  const updateField = useCallback((index: number, updatedField: FormField) => {
    setFields(prev => prev.map((field, i) => i === index ? updatedField : field));
  }, []);

  const deleteField = useCallback((index: number) => {
    setFields(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addSection = useCallback(() => {
    const newSection: FormSection = {
      id: `section_${Date.now()}`,
      title: 'Ny sektion',
      description: '',
      defaultExpanded: true,
      order: sections.length,
    };
    setSections(prev => [...prev, newSection]);
  }, [sections.length]);

  const updateSection = useCallback((index: number, updatedSection: FormSection) => {
    setSections(prev => prev.map((section, i) => i === index ? updatedSection : section));
  }, []);

  const deleteSection = useCallback((index: number) => {
    const sectionToDelete = sections[index];
    if (!sectionToDelete) return;

    // Remove section assignment from fields that belong to this section
    setFields(prev => prev.map(field => {
      if (field.sectionId === sectionToDelete.id) {
        const { sectionId, ...rest } = field;
        return rest as FormField;
      }
      return field;
    }));
    setSections(prev => prev.filter((_, i) => i !== index));
  }, [sections]);

  /**
   * Persists the draft through the caller and, on success, makes the draft the new
   * baseline so the dialog can stay open with everything the admin just built.
   *
   * Form-field and section order live in the formConfiguration blob; requirement
   * displayOrder is a DB column and needs its own bulk write. Both go in one draft so
   * the caller can sequence the writes before it refetches. documentRequirements are
   * NOT written into the blob: they live in their own table, and the stale copy that
   * used to go here is what made the old drag-reorder appear to work while silently
   * changing nothing.
   */
  const handleSave = async (closeAfter: boolean) => {
    setPendingAction(closeAfter ? 'saveAndClose' : 'save');
    try {
      await onSave({
        formConfiguration: { fields, sections },
        requirementSortOrders: toRequirementSortOrders(documentRequirements),
      });
      setSavedFormSnapshot(snapshotForm(fields, sections));
      setRequirementOrderDirty(false);
      if (closeAfter) resetAndClose();
    } catch {
      // The caller's mutation has already shown the error; the draft stays put.
    } finally {
      setPendingAction(null);
    }
  };

  const resetAndClose = () => {
    setFields([]);
    setSections([]);
    setDocumentRequirements([]);
    setCurrentTab(0);
    setPreviewMode(false);
    setConfirmCloseOpen(false);
    onClose();
  };

  const isSaving = isLoading || pendingAction !== null;

  /**
   * Close from Avbryt, Escape or the backdrop: ask first if the draft is unsaved.
   * Ignored while a save is in flight, so the dialog cannot be torn down and then
   * closed a second time when the pending save resolves.
   */
  const requestClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmCloseOpen(true);
      return;
    }
    resetAndClose();
  };

  const renderFieldPreview = (field: FormField) => (
    <Box>
      <Typography variant="body2" gutterBottom sx={{
        fontWeight: "medium"
      }}>
        {field.label}
        {field.required && <span style={{ color: 'red' }}> *</span>}
      </Typography>
      
      {field.type === 'text' && (
        <TextField
          fullWidth
          size="small"
          {...(field.placeholder && { placeholder: field.placeholder })}
          disabled
        />
      )}

      {field.type === 'textarea' && (
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          {...(field.placeholder && { placeholder: field.placeholder })}
          disabled
        />
      )}

      {field.type === 'number' && (
        <TextField
          fullWidth
          size="small"
          type="number"
          {...(field.placeholder && { placeholder: field.placeholder })}
          disabled
        />
      )}
      
      {field.type === 'date' && (
        <AppDatePicker value={null} onChange={() => {}} size="small" disabled />
      )}
      
      {(field.type === 'select' || field.type === 'multiselect') && (
        <Box>
          <TextField
            fullWidth
            size="small"
            select
            disabled
            placeholder="Välj alternativ"
          />
          <Box
            sx={{
              mt: 1,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5
            }}>
            {field.options?.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
      
      {field.type === 'boolean' && (
        <FormControlLabel
          control={<Switch disabled />}
          label="Ja/Nej"
        />
      )}
      
      {field.description && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: "block",
            mt: 0.5
          }}>
          {field.description}
        </Typography>
      )}
    </Box>
  );

  const renderFormPreview = () => {
    // Organize fields by sections
    const fieldsWithoutSection = fields.filter(field => !field.sectionId);
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);

    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Förhandsvisning av formulär
        </Typography>
        <Typography variant="body2" gutterBottom sx={{
          color: "text.secondary"
        }}>
          Så här kommer formuläret att se ut för slutanvändare:
        </Typography>

        <Divider sx={{ my: 2 }} />

        {fields.length === 0 ? (
          <Alert severity="info">
            Inga fält har lagts till ännu. Lägg till fält för att se förhandsvisningen.
          </Alert>
        ) : (
          <Box>
            {/* Fields without sections */}
            {fieldsWithoutSection.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {fieldsWithoutSection.map((field) => (
                  <Grid size={{ xs: 12, sm: field.layoutWidth }} key={field.id}>
                    {renderFieldPreview(field)}
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Fields organized by sections */}
            {sortedSections.map((section) => {
              const sectionFields = fields.filter(field => field.sectionId === section.id);
              
              if (sectionFields.length === 0) return null;

              return (
                <Accordion 
                  key={section.id} 
                  defaultExpanded={section.defaultExpanded}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`section-${section.id}-content`}
                    id={`section-${section.id}-header`}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: "medium"
                      }}>
                        {section.title}
                      </Typography>
                      {section.description && (
                        <Typography variant="caption" sx={{
                          color: "text.secondary"
                        }}>
                          {section.description}
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {sectionFields.map((field) => (
                        <Grid size={{ xs: 12, sm: field.layoutWidth }} key={field.id}>
                          {renderFieldPreview(field)}
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </Paper>
    );
  };

  if (!controlType) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: '90vh' }
        }
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Box>
            <Typography variant="h5">
              Konfigurera formulär
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {controlType.name}
            </Typography>
          </Box>
          <Button
            variant={previewMode ? "contained" : "outlined"}
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? 'Redigera' : 'Förhandsvisa'}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {previewMode ? (
          renderFormPreview()
        ) : (
          <Box>
            <Tabs value={currentTab} onChange={(_e, newValue) => setCurrentTab(newValue)}>
              <Tab label="Formulärfält" />
              <Tab label="Sektioner" />
              <Tab label="Dokumentkrav" />
              <Tab label="Sortering" />
            </Tabs>

            <TabPanel value={currentTab} index={0}>
              <Box sx={{
                mb: 3
              }}>
                <Typography variant="h6" gutterBottom>
                  Formulärfält
                </Typography>
                <Typography variant="body2" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  Lägg till och konfigurera fält som användare kommer att fylla i när de skapar kontroller av denna typ.
                </Typography>
                
                {/* Quick Add Templates */}
                <Box sx={{
                  my: 2
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Snabblägga vanliga fält:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1
                    }}>
                    {FIELD_TEMPLATES.map((template, index) => (
                      <Chip
                        key={index}
                        label={`${template.label} (${template.type})`}
                        onClick={() => addField(template)}
                        clickable
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    {fields.length} fält konfigurerade
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => addField()}
                  >
                    Lägg till fält
                  </Button>
                </Box>
              </Box>

              {fields.length === 0 ? (
                <Alert severity="info">
                  Inga fält har lagts till ännu. Klicka på "Lägg till fält" eller välj från snabbmallarna ovan.
                </Alert>
              ) : (
                <Box>
                  {fields.map((field, index) => (
                    <FormFieldEditor
                      key={field.id}
                      field={field}
                      onUpdate={(updatedField) => updateField(index, updatedField)}
                      onDelete={() => deleteField(index)}
                      availableSections={sections}
                    />
                  ))}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
              <Box sx={{
                mb: 3
              }}>
                <Typography variant="h6" gutterBottom>
                  Formulärsektioner
                </Typography>
                <Typography variant="body2" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  Skapa sektioner för att organisera och gruppera formulärfält. Sektioner kan vara expanderbara och ha villkorlig visning.
                </Typography>
                
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                  }}>
                  <Typography variant="body2" sx={{
                    color: "text.secondary"
                  }}>
                    {sections.length} sektioner konfigurerade
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addSection}
                  >
                    Lägg till sektion
                  </Button>
                </Box>
              </Box>

              {sections.length === 0 ? (
                <Alert severity="info">
                  Inga sektioner har skapats ännu. Sektioner hjälper till att organisera stora formulär i hanterbara grupper.
                </Alert>
              ) : (
                <Box>
                  {sections.map((section, index) => (
                    <FormSectionEditor
                      key={section.id}
                      section={section}
                      onUpdate={(updatedSection) => updateSection(index, updatedSection)}
                      onDelete={() => deleteSection(index)}
                    />
                  ))}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={currentTab} index={2}>
              <DocumentRequirementEditor
                requirements={documentRequirements}
                onChange={setDocumentRequirements}
                disabled={isLoading}
                controlTypeId={controlType?.id || ''}
                periodConfig={{
                  periodMode: controlType.periodMode ?? DEFAULT_PERIOD_CONFIG.periodMode,
                  periodYearOffset: controlType.periodYearOffset ?? DEFAULT_PERIOD_CONFIG.periodYearOffset,
                  periodMonths: controlType.periodMonths ?? null,
                }}
                sections={sections}
              />
            </TabPanel>

            <TabPanel value={currentTab} index={3}>
              <SortingTab
                fields={fields}
                sections={sections}
                documentRequirements={documentRequirements}
                disabled={isLoading}
                onChange={(next) => {
                  setFields(next.fields);
                  setSections(next.sections);
                  // SortingTab rebuilds the array on every drag, so compare the
                  // order itself rather than the reference.
                  if (
                    JSON.stringify(toRequirementSortOrders(next.documentRequirements)) !==
                    JSON.stringify(toRequirementSortOrders(documentRequirements))
                  ) {
                    setRequirementOrderDirty(true);
                  }
                  setDocumentRequirements(next.documentRequirements);
                }}
              />
            </TabPanel>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={requestClose} disabled={isSaving}>
          Avbryt
        </Button>
        {/* Spara keeps the dialog open so an admin adding several fields can save
            as they go; Spara och stäng is the old single-step flow. */}
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => handleSave(false)}
          disabled={isSaving}
        >
          {pendingAction === 'save' ? 'Sparar...' : 'Spara'}
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => handleSave(true)}
          disabled={isSaving}
        >
          {pendingAction === 'saveAndClose' ? 'Sparar...' : 'Spara och stäng'}
        </Button>
      </DialogActions>

      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Osparade ändringar</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Du har ändringar som inte är sparade. Vill du stänga utan att spara?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCloseOpen(false)}>
            Fortsätt redigera
          </Button>
          <Button color="error" onClick={resetAndClose} disabled={isSaving}>
            Stäng utan att spara
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmCloseOpen(false);
              void handleSave(true);
            }}
            disabled={isSaving}
          >
            Spara och stäng
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FormConfigurationBuilder;